// One merge request (spec/dashboard.md section 12.3; spec/api-conventions.md section 12.2): `GET …/merge-requests/{mr}`
// re-read after every action (never polled); the title and description edited in place (`PATCH` with `If-Match`), the
// state, `source → target` with the three shas, the counts, the approvals, mergeable and why not; the Changes tab (the
// diff with `include=content` through the changes grid, on the target) and the Conflicts tab (the three-way resolver);
// the actions Approve / Unapprove, Merge…, Close / Reopen. The Conversation tab (`?tab=conversation`, Conversation.tsx; the
// label carries the count and an unresolved marker); the summary shows "N comments · M unresolved" beside the approvals and
// the Mergeable line warns about unresolved comments without blocking (api-conventions section 12.2 "Comments").
import { useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ops } from '../../api/operations';
import type { MergeDiff, MergeRequest } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ChangesGrid } from '../../history/ChangesGrid';
import type { ContentChange } from '../../history/changes';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { AlertCircle, ArrowLeft, Check, GitMerge, Pencil } from '../../ui/icons';
import { CommitTextInput } from '../../ui/inputs';
import { Tabs } from '../../ui/Tabs';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { ConflictResolver } from './ConflictResolver';
import { commentCounts, commentSummary, Conversation } from './Conversation';
import { MergeDialog } from './MergeDialog';
import { asApiError, mrKey, mrPrefix, notMergeableReasons, plural, shortSha, STATE_TONE } from './merge';
import './merge.css';
import { LoadingPane } from '../../ui/PageStates';

export function MergeRequestPage() {
  const { branch = '', mr: mrParam = '' } = useParams();
  const { org, game, offsets } = useGame();
  const { client } = useAuth();
  const [zone] = useTimeZone();
  const [params] = useSearchParams();
  const key = mrKey(org, game.slug, mrParam);
  const mrQ = useQuery(key, () => client.data(ops.getMergeRequest, { path: { org, game: game.slug, mr: mrParam } }) as unknown as Promise<MergeRequest>);
  const mr = mrQ.data as MergeRequest | undefined;
  const error = mrQ.error ? asApiError(mrQ.error) : null;
  const reread = () => queryCache.invalidate(mrPrefix(org, game.slug));

  if (error && !mr) {
    return (
      <section className="mr-page" data-testid="merge-request">
        <div className="page-head">
          <h1>Merge request</h1>
        </div>
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">
            {error.status === 404 ? `No merge request !${mrParam} in this game.` : error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </div>
          <Button size="s" onClick={() => queryCache.invalidate(key)}>
            Retry
          </Button>
        </div>
        <p className="mr-back">
          <Link to={routes.mergeRequests(org, game.slug, branch)}>All merge requests</Link>
        </p>
      </section>
    );
  }
  if (!mr) {
    return (
      <section className="mr-page" data-testid="merge-request">
        <LoadingPane rows={5} label="Loading the merge request" />
      </section>
    );
  }

  const when = (v: string | null | undefined) => formatInstant(v, zone, { offsets });
  const tabParam = params.get('tab');
  const tab = tabParam === 'conflicts' || tabParam === 'conversation' ? tabParam : 'changes';
  const reasons = notMergeableReasons(mr);
  const counts = commentCounts(mr);
  const history = (b: string, sha: string | null | undefined) =>
    sha ? (
      <Link to={routes.history(org, game.slug, b, sha)}>
        <code>{shortSha(sha)}</code>
      </Link>
    ) : (
      <code>—</code>
    );
  return (
    <section className="mr-page" data-testid="merge-request">
      <p className="mr-back">
        <Link to={routes.mergeRequests(org, game.slug, branch)}><ArrowLeft className="icon-s" aria-hidden="true" /> Merge requests</Link>
      </p>
      <div className="page-head mr-head">
        <span className="mr-number">!{String(mr.number)}</span>
        <EditableText mr={mr} field="title" onSaved={reread} />
        <span data-testid="mr-state">
          <Badge tone={STATE_TONE[mr.state ?? ''] ?? 'neutral'}>{mr.state}</Badge>
        </span>
        {mr.alreadyMerged && <Badge tone="success">already merged</Badge>}
        <div className="spacer" />
        <Actions mr={mr} onDone={reread} />
      </div>
      <div className="pane mr-summary">
        <div className="pane-head">
          <GitMerge className="icon" aria-hidden="true" />
          <span className="mr-branches" data-testid="mr-branches">
            <code>{mr.sourceBranch}</code> → <code>{mr.targetBranch}</code>
            {mr.targetProtected && (
              <>
                {' '}
                <Badge tone="warning">protected target</Badge>
              </>
            )}
          </span>
          <div className="spacer" />
          <span className={`status-dot ${reasons.length === 0 ? 'status-live' : 'status-ended'}`}>{reasons.length === 0 ? 'Mergeable' : 'Not mergeable'}</span>
        </div>
        <div className="pane-body">
      <EditableText mr={mr} field="description" onSaved={reread} />
      <dl className="kv mr-meta">
        <Row label="Commits">
          <span className="mr-shas" data-testid="mr-shas">
            base {history(mr.targetBranch, mr.baseSha)} · target {history(mr.targetBranch, mr.targetSha)} · source {history(mr.sourceBranch, mr.sourceSha)}
          </span>
        </Row>
        <Row label="Counts">
          <span data-testid="mr-counts">
            {plural(mr.changes, 'change')} · {String(mr.unresolved)} unresolved
          </span>
        </Row>
        <Row label="Approvals">
          <div data-testid="mr-approvals">
            {(mr.approvals ?? []).length === 0 ? (
              <span className="muted">none</span>
            ) : (
              <ul className="mr-approvals">
                {(mr.approvals ?? []).map((a) => (
                  <li key={`${a.actor.id}:${a.sourceSha}`}>
                    {a.actor.label} <span className="muted small">{when(a.at)}</span>{' '}
                    {a.current ? (
                      <Badge tone="success">current</Badge>
                    ) : (
                      <Badge tone="warning" title={`Given on ${shortSha(a.sourceSha)}; the source moved since`}>
                        lapsed
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Row>
        <Row label="Comments">
          <span data-testid="mr-comments">
            {commentSummary(mr)}
          </span>
        </Row>
        <Row label="Mergeable">
          <span data-testid="mr-mergeable">{reasons.length === 0 ? 'Mergeable' : `Not mergeable: ${reasons.join('; ')}`}</span>
          {counts.unresolved > 0 && mr.state === 'open' && (
            <>
              {' '}
              <span data-testid="mr-comments-warning">
                <Badge tone="warning" title="The server does not block the merge on comments">
                  {plural(counts.unresolved, 'unresolved comment')}
                </Badge>
              </span>
            </>
          )}
        </Row>
        {mr.mergeCommit && (
          <Row label="Merge commit">
            <span data-testid="mr-merge-commit">
              {history(mr.targetBranch, mr.mergeCommit)}
              {mr.mergedBy ? ` by ${mr.mergedBy.label}` : ''} {when(mr.mergedAt)}
            </span>
          </Row>
        )}
        {mr.closedAt && <Row label="Closed">{when(mr.closedAt)}</Row>}
        <Row label="Author">
          {mr.createdBy?.label ?? '—'} <span className="muted small">{when(mr.createdAt)}</span>
        </Row>
        <Row label="Updated">{when(mr.updatedAt)}</Row>
      </dl>
        </div>
      </div>
      <Tabs
        param="tab"
        ariaLabel="Merge request tabs"
        items={[
          { key: 'changes', label: 'Changes', count: Number(mr.changes) },
          { key: 'conflicts', label: 'Conflicts', count: (mr.conflicts ?? []).length },
          {
            key: 'conversation',
            label: (
              <span data-testid="mr-conversation-tab">
                Conversation{counts.total > 0 ? ` · ${counts.total}` : ''}
                {counts.unresolved > 0 && (
                  <>
                    {' '}
                    <Badge tone="warning">{counts.unresolved} unresolved</Badge>
                  </>
                )}
              </span>
            ),
          },
        ]}
      />
      {tab === 'changes' ? <ChangesTab mr={mr} /> : tab === 'conflicts' ? <ConflictResolver mr={mr} /> : <Conversation mr={mr} />}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

function ChangesTab({ mr }: { mr: MergeRequest }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const id = String(mr.number);
  const key = mrKey(org, game.slug, id, `diff@${mr.sourceSha}:${mr.targetSha}`);
  const diffQ = useQuery(key, () => client.data(ops.getMergeRequestDiff, { path: { org, game: game.slug, mr: id }, query: { include: 'content' } }) as unknown as Promise<MergeDiff>);
  const diff = diffQ.data as MergeDiff | undefined;
  const error = diffQ.error ? asApiError(diffQ.error) : null;
  return (
    <div data-testid="mr-changes">
      {error ? (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </div>
          <Button size="s" onClick={() => queryCache.invalidate(key)}>
            Retry
          </Button>
        </div>
      ) : !diff ? (
        <LoadingPane rows={3} label="Loading the changes" />
      ) : (
        <ChangesGrid
          changes={(diff.changes ?? []) as ContentChange[]}
          branch={mr.targetBranch}
          contentTruncated={diff.contentTruncated === true}
          emptyMessage="Merging changes nothing on the target."
        />
      )}
    </div>
  );
}

/** The title or the description, edited in place: a pencil opens a `CommitTextInput`; a commit is a `PATCH` with `If-Match`. */
function EditableText({ mr, field, onSaved }: { mr: MergeRequest; field: 'title' | 'description'; onSaved: () => void }) {
  const { org, game, can } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const canWrite = can('content_write');
  const value = (field === 'title' ? mr.title : mr.description) ?? '';
  const label = field === 'title' ? 'Title' : 'Description';

  const save = async (text: string) => {
    const next = field === 'title' ? text.trim() : text;
    if (field === 'title' && next === '') {
      toasts.error('The title cannot be empty.');
      return;
    }
    setBusy(true);
    try {
      await client.data(ops.updateMergeRequest, { path: { org, game: game.slug, mr: String(mr.number) }, body: { [field]: next }, ifMatch: Number(mr.version) });
      toasts.success(`${label} saved`);
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.isVersionConflict ? 'Someone changed this merge request meanwhile; it was re-read.' : err.message, { requestId: err.requestId });
    } finally {
      setBusy(false);
      setEditing(false);
      onSaved();
    }
  };

  const shown =
    field === 'title' ? (
      <h1 className="mr-title" data-testid="mr-title">
        {mr.title}
      </h1>
    ) : (
      <p className={`mr-description ${value ? '' : 'muted'}`} data-testid="mr-description">
        {value || 'No description.'}
      </p>
    );
  if (!editing) {
    return (
      <div className={`mr-editable mr-editable-${field}`}>
        {shown}
        <IconButton label={`Edit ${field}`} onClick={() => setEditing(true)} disabled={!canWrite || busy} title={canWrite ? `Edit ${field}` : "You lack the 'content_write' permission."}>
          <Pencil aria-hidden="true" />
        </IconButton>
      </div>
    );
  }
  return (
    <span
      className={`mr-editable mr-editable-${field}`}
      onBlur={() => setEditing(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setEditing(false);
      }}
    >
      <CommitTextInput aria-label={label} value={value} onCommit={(v) => void save(v)} multiline={field === 'description'} autoFocus disabled={busy} />
    </span>
  );
}

function Actions({ mr, onDone }: { mr: MergeRequest; onDone: () => void }) {
  const { org, game, can, permissions, refreshBranches } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);
  const path = { org, game: game.slug, mr: String(mr.number) };
  const open = mr.state === 'open';
  const me = permissions?.actor?.id;
  const isAuthor = !!me && mr.createdBy?.id === me;
  const mine = (mr.approvals ?? []).filter((a) => a.actor.id === me);
  const approvedNow = mine.some((a) => a.current);
  const approver = can('content_write') || can('deploy', 'prod') ? null : 'content_write';
  const writer = can('content_write') ? null : 'content_write';

  const run = async (name: string, call: () => Promise<unknown>, done: string) => {
    setBusy(name);
    try {
      await call();
      toasts.success(done);
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setBusy(null);
      onDone();
    }
  };
  const approve = () => run('approve', () => client.data(ops.approveMergeRequest, { path }), `Approved !${mr.number}`);
  const unapprove = () => run('unapprove', () => client.data(ops.unapproveMergeRequest, { path }), `Approval of !${mr.number} withdrawn`);
  const close = async () => {
    if (!(await confirm({ title: `Close !${mr.number}?`, body: 'The request keeps the tips it has now; it can be reopened.', confirmLabel: 'Close', tone: 'danger' }))) return;
    await run('close', () => client.data(ops.closeMergeRequest, { path }), `Closed !${mr.number}`);
  };
  const reopen = async () => {
    if (!(await confirm({ title: `Reopen !${mr.number}?`, body: 'The request is computed live again from the branch tips.', confirmLabel: 'Reopen' }))) return;
    await run('reopen', () => client.data(ops.reopenMergeRequest, { path }), `Reopened !${mr.number}`);
  };

  const approveTitle = isAuthor ? 'The author cannot approve' : !open ? `The request is ${mr.state}.` : approvedNow ? 'You approved the current source tip.' : 'Approve the current source tip';
  return (
    <>
      <div className="mr-actions" role="toolbar" aria-label="Merge request actions">
        <Button icon={<Check aria-hidden="true" />} onClick={() => void approve()} busy={busy === 'approve'} disabled={isAuthor || !open || approvedNow} missingPermission={approver} title={approveTitle}>
          Approve
        </Button>
        <Button
          onClick={() => void unapprove()}
          busy={busy === 'unapprove'}
          disabled={!open || mine.length === 0}
          missingPermission={approver}
          title={mine.length === 0 ? 'You have not approved this request.' : 'Withdraw your approval'}
        >
          Unapprove
        </Button>
        {open ? (
          <Button variant="danger" onClick={() => void close()} busy={busy === 'close'} missingPermission={writer}>
            Close
          </Button>
        ) : (
          <Button
            onClick={() => void reopen()}
            busy={busy === 'reopen'}
            disabled={mr.state === 'merged'}
            missingPermission={writer}
            title={mr.state === 'merged' ? 'A merged request cannot be reopened' : 'Reopen the request'}
          >
            Reopen
          </Button>
        )}
        <Button variant="primary" icon={<GitMerge aria-hidden="true" />} onClick={() => setMerging(true)} disabled={!open} title={open ? `Merge ${mr.sourceBranch} into ${mr.targetBranch}` : `The request is ${mr.state}.`}>
          Merge…
        </Button>
      </div>
      {merging && (
        <MergeDialog
          mr={mr}
          onClose={() => setMerging(false)}
          onMerged={() => {
            refreshBranches();
            onDone();
          }}
        />
      )}
    </>
  );
}
